import { FiShield, FiZap, FiGlobe, FiCpu, FiLayout } from "react-icons/fi";
import { SiLaravel, SiPhp, SiVite } from "react-icons/si";
import { LuSettings, LuUsers, LuShieldCheck, LuPercent, LuShield, LuCpu, LuClipboardList, LuCode, LuCircleCheck, LuBug, LuRocket, LuDatabase, LuClock } from "react-icons/lu";

export const laravelBenefits = [
  {
    icon: <FiLayout className="w-8 h-8" />,
    title: "Elegant Syntax",
    desc: "Laravel's expressive and clean syntax allows for rapid development without sacrificing code quality.",
  },
  {
    icon: <FiZap className="w-8 h-8" />,
    title: "Eloquent ORM",
    desc: "One of the most powerful and intuitive database toolkits for handling complex data relationships.",
  },
  {
    icon: <FiShield className="w-8 h-8" />,
    title: "Secure by Design",
    desc: "Built-in protection against SQL injection, cross-site request forgery, and cross-site scripting.",
  },
];

export const laravelServices = [
  {
    icon: <SiLaravel size={36} />,
    title: "Custom Laravel App",
    description: "Developing bespoke server-side applications with the world's most popular PHP framework.",
    image: "/images/ecommerce-dev.jpg",
    link: "#",
  },
  {
    icon: <SiVite size={36} />,
    title: "InertiaJS & Vue/React",
    description: "Building modern SPAs with the simplicity of server-side routing using Inertia.js.",
    image: "/images/fullstack-dev.jpg",
    link: "#",
  },
  {
    icon: <FiCpu size={36} />,
    title: "Laravel Ecommerce",
    description: "Designing high-performance online stores with Laravel's robust commerce ecosystems.",
    image: "/images/webapp-dev.jpg",
    link: "#",
  },
];

export const laravelFaqs = [
  {
    q: "Why should I choose Laravel development in India for my web application?",
    a: "Laravel development in India offers cost-effective solutions with skilled developers who can build secure, scalable, and high-performance web applications. Laravel’s modern PHP framework allows for faster development and easy maintenance.",
  },
  {
    q: "What services does a Laravel development company in India provide?",
    a: "A Laravel development company in India provides end-to-end services including custom web app development, API integration, e-commerce solutions, database management, and ongoing support. They ensure your web application is robust, secure, and tailored to your business needs.",
  },
  {
    q: "What can Laravel developers do for my business?",
    a: "Laravel developers design and build web applications using the Laravel framework. They handle backend logic, database integration, API connections, and ensure your application is fast, secure, and scalable while providing an excellent user experience.",
  },
];

export const laravelProcessSteps = [
  {
    step: "01",
    title: "Feature Blueprinting",
    description: "Mapping out the routes, controllers, and Eloquent models for your application.",
    points: ["Route definition", "Model mapping", "Migration design"],
  },
  {
    step: "02",
    title: "Rapid Development",
    description: "Building high-quality features in record time using Laravel's rich suite of tools.",
    points: ["Eloquent logic", "Service classes", "Event/Listener setup"],
  },
  {
    step: "03",
    title: "Testing & Forge",
    description: "Ensuring 100% stability with Pest/PHPUnit and deploying seamlessly via Laravel Forge.",
    points: ["Automated testing", "CI/CD setup", "Forge deployment"],
  },
];

export const laravelTechStack = ["Laravel", "PHP", "Eloquent", "Livewire", "Inertia.js", "Redis", "Vite", "Pest"];

export const laravelHeroData = {
  backgroundImage: "/images/serviceBanners/LaravelBanner.webp",
  whiteImage: "/images/serviceBanners/LaravelBannerW.webp",
  subTitle: "Laravel Development Services",
  title: (
    <>
      Elegant <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Laravel Applications</span>
    </>
  ),
  description:
    "We build elegant, robust, and scalable web applications using Laravel’s expressive syntax and powerful ecosystem to deliver secure, high performance, and feature rich backend solutions for modern business requirements.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("Get Started clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Eloquent ORM",
      desc: "Simple and powerful database mapping for Laravel applications.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Blade / Livewire",
      desc: "Build dynamic and reactive UI components with Blade templates and Livewire.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Authentication System",
      desc: "Secure built-in authentication with guards, passwords, and user management.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Laravel Forge",
      desc: "Easy server deployment and scaling with automated infrastructure management.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp", // Re-use generic icon or replace with custom Laravel path asset
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

export const laravelStatsData = [
  {
    icon: LuSettings,
    value: "250+",
    label: "Laravel Projects",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "110+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.96%",
    label: "Service Uptime",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuCpu,
    value: "15M+",
    label: "API Requests",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "45%",
    label: "Time-to-Market Saved",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Secure Deployments",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

export const laravelSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "Laravel Backend Solutions We Build",
  buildCards: [
    {
      title: "eCommerce Web Systems",
      desc: "Build scalable Laravel-based shopping systems with secure payments and cart management using Eloquent.",
      icon: LuSettings,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "InertiaJS SPAs",
      desc: "Create modern single-page applications using Vue or React with seamless Laravel backend integration.",
      icon: LuCpu,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "RESTful API Hubs",
      desc: "Develop clean and structured APIs with validation, pagination, and resource transformation.",
      icon: LuCode,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Custom SaaS Applications",
      desc: "Build subscription-based platforms with Laravel Cashier and multi-tenant support.",
      icon: LuClock,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Queue & Job Processing",
      desc: "Handle background tasks efficiently using Redis queues and Laravel Horizon.",
      icon: LuDatabase,
      colorClass: "text-[#6366f1]",
      bgClass: "bg-[#6366f1]/10",
      borderClass: "border-[#6366f1]/20 hover:border-[#6366f1]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(99,102,241,0.25)]",
    },
    {
      title: "Automated Testing Suite",
      desc: "Ensure stable releases with robust unit and feature testing using Pest framework.",
      icon: LuRocket,
      colorClass: "text-[#d946ef]",
      bgClass: "bg-[#d946ef]/10",
      borderClass: "border-[#d946ef]/20 hover:border-[#d946ef]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(217,70,239,0.25)]",
    },
  ],
  processSubtitle: "LARAVEL ENGINEERING PROCESS",
  processTitle: "Laravel Engineering Process",
  processSteps: [
    {
      step: "01",
      title: "Database Mapping",
      description: "Design migrations, seeders, and Eloquent relationships for structured data flow.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Routing & Authorization",
      description: "Define web/api routes with secure policies and role-based access control.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Core Development",
      description: "Build controllers, Form Requests, and service-layer business logic.",
      icon: LuSettings,
    },
    {
      step: "04",
      title: "Async Processing",
      description: "Implement queues, events, listeners, and mail jobs for background tasks.",
      icon: LuShieldCheck,
    },
    {
      step: "05",
      title: "Testing (Pest/PHPUnit)",
      description: "Ensure reliability with unit and feature tests covering key workflows dynamically.",
      icon: LuCircleCheck, // Build-compliant icon to preserve fast cross-platform deployment safety
    },
    {
      step: "06",
      title: "Deployment (Forge/Vapor)",
      description: "Automate deployment using Laravel Forge or Vapor for scalable hosting.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE LARAVEL?",
  benefitsTitle: "Why Laravel is the Right Choice",
  benefits: [
    "Elegant syntax speed",
    "Powerful Eloquent ORM",
    "Blade Livewire support",
    "Built-in security defaults",
    "Queue system reliability",
    "Large developer ecosystem",
    "Forge Vapor deployment",
  ],
  logoImage: "/images/serviceBanners/LaravelBanner.webp",
  whiteLogoImage: "/images/serviceBanners/LaravelBannerW.webp",
};

export const laravelPortfolioData = {
  subtitle: "OUR WORK",
  title: "Laravel Solutions We're Proud Of",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "CartCraft eCommerce",
      badge: "Laravel & Redis Shop",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Fast, custom eCommerce store with multi-currency checkout and optimized product searching.",
      downloads: "20M+ Turnover",
      rating: "5.0",
      image: "/images/WebAppDevelopment/Laravel_Development.png",
    },
    {
      title: "SaaS Workspace Gateway",
      badge: "InertiaJS & Vue",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "Beautiful user workspace manager for team collaborations utilizing Laravel Cashier.",
      downloads: "50k+ Subscriptions",
      rating: "4.9",
      image: "/images/WebAppDevelopment/Laravel_Development.png",
    },
  ],
};

export const laravelTechnologiesData = {
  techTitle: "TECHNOLOGIES WE USE",
  technologies: [
    { name: "REST API", icon: "RestAPI" },
    { name: "SQLite", icon: "SQLite" },
    { name: "Firebase", icon: "Firebase" },
    { name: "Provider", icon: "Provider" },
  ],
  trustTitle: "TRUSTED BY BUSINESSES WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};
export const laravelAboutData = [
  {
    title: "Laravel Development Services for Scalable Web Applications",
    content: (
      <>
        Modern businesses require web applications that remain flexible, secure, and easy to scale as customer expectations grow. Reliable{" "}
        <b className="text-text-primary font-semibold">laravel development services</b> help organizations build structured digital systems that improve operational efficiency and
        support long-term business growth. Many companies struggle with outdated application frameworks that limit flexibility and performance. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, Laravel solutions are developed around business objectives, usability, and scalability to help
        organizations create dependable digital products designed for future growth.
      </>
    ),
  },
  {
    title: "Laravel Development Company for Better Business Systems",
    content: (
      <>
        A successful web application depends on how efficiently it handles workflows, customer interactions, and business operations. A trusted{" "}
        <b className="text-text-primary font-semibold">laravel development company</b> helps businesses create stable backend systems that improve application performance and
        simplify digital processes. Companies investing in <b className="text-text-primary font-semibold">laravel web development</b> often benefit from stronger maintainability
        because applications are designed to adapt to evolving business requirements. Tycho Technologies focuses on practical Laravel frameworks that improve system reliability
        while supporting business continuity and digital transformation.
      </>
    ),
  },
  {
    title: "Hire Laravel Developers for Smarter Digital Infrastructure",
    content: (
      <>
        Businesses managing growing digital ecosystems require backend environments that remain adaptable and performance-focused over time. Companies looking to{" "}
        <b className="text-text-primary font-semibold">hire laravel developers</b> often prioritize flexibility, maintainability, and scalable application performance for long-term
        success. Organizations aiming to strengthen backend ecosystems frequently combine modern frameworks with{" "}
        <a href="https://tychotechnologies.com/codeigniter-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          codeigniter development services
        </a>{" "}
        to improve digital adaptability and support changing business demands.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Laravel Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">laravel development company in india</b> continues to grow as businesses seek scalable and
        future-ready web application frameworks. Companies investing in <b className="text-text-primary font-semibold">laravel development services india</b> often focus on
        operational stability, better application flexibility, and stronger performance outcomes. Businesses partnering with Tycho Technologies gain access to customized Laravel
        strategies designed around measurable business objectives and technical requirements. Many organizations also strengthen backend capabilities through{" "}
        <a href="https://tychotechnologies.com/symfony-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          php symfony development
        </a>{" "}
        to support connected enterprise systems and long-term scalability.
      </>
    ),
  },
];
